import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Upload as UploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { settingsService } from '../../services/settings';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const LANDING_IMAGES = [
  {
    slot: 'hero',
    field: 'landing_hero_image',
    label: 'Hero',
    helper: 'Wide image behind the event title.',
    fallback: '/theme/oc-bar.png',
  },
  {
    slot: 'gaming',
    field: 'landing_gaming_image',
    label: 'Gaming Area',
    helper: 'Left photo in The Venue section.',
    fallback: '/theme/oc-lan-wide.png',
  },
  {
    slot: 'bar',
    field: 'landing_bar_image',
    label: 'Bar & Lounge',
    helper: 'Right photo in The Venue section.',
    fallback: '/theme/oc-bar-detail.png',
  },
];

function SystemSettings() {
  const { settings, refreshSettings } = useSettings();
  const [formData, setFormData] = useState({
    app_title: settings.app_title || '',
    nav_title: settings.nav_title || ''
  });
  const [loading, setLoading] = useState(false);
  const [imageBusy, setImageBusy] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputs = useRef({});

  // Update form when settings change
  React.useEffect(() => {
    setFormData({
      app_title: settings.app_title || '',
      nav_title: settings.nav_title || ''
    });
  }, [settings]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.app_title.trim()) {
      setError('App title is required');
      setLoading(false);
      return;
    }
    if (!formData.nav_title.trim()) {
      setError('Navigation title is required');
      setLoading(false);
      return;
    }

    const result = await settingsService.updateSettings(formData);

    if (result.success) {
      setSuccess('Settings saved successfully!');
      await refreshSettings();
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleImageUpload = async (slot, file) => {
    if (!file) return;
    setImageBusy(slot);
    setError(null);
    setSuccess(null);
    const result = await settingsService.uploadLandingImage(slot, file);
    if (result.success) {
      setSuccess('Landing image updated.');
      await refreshSettings();
    } else {
      setError(result.error);
    }
    setImageBusy(null);
  };

  const handleImageClear = async (slot) => {
    setImageBusy(slot);
    setError(null);
    setSuccess(null);
    const result = await settingsService.clearLandingImage(slot);
    if (result.success) {
      setSuccess('Landing image reset to the placeholder.');
      await refreshSettings();
    } else {
      setError(result.error);
    }
    setImageBusy(null);
  };

  const handleReset = () => {
    setFormData({
      app_title: settings.app_title || '',
      nav_title: settings.nav_title || ''
    });
    setError(null);
    setSuccess(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            System Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure application-wide settings
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Application Titles
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Browser Tab Title"
            name="app_title"
            value={formData.app_title}
            onChange={handleChange}
            placeholder="OC-LANgenthal Vol. 3 - 01.01.26 - 04.01.26"
            helperText="This title appears in the browser tab and bookmarks"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Navigation Title"
            name="nav_title"
            value={formData.nav_title}
            onChange={handleChange}
            placeholder="OC-LANgenthal"
            helperText="This title appears in the navigation bar and mobile menu"
            disabled={loading}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Landing Page Images
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a replacement for each photo. Until you do, the page keeps the built-in placeholder.
        </Typography>
        <Grid container spacing={2}>
          {LANDING_IMAGES.map((image) => {
            const uploaded = settings[image.field];
            const preview = uploaded ? resolveMediaUrl(uploaded) : image.fallback;
            const busy = imageBusy === image.slot;
            return (
              <Grid item xs={12} md={4} key={image.slot}>
                <Box
                  sx={{
                    height: 140,
                    mb: 1.5,
                    borderRadius: 1,
                    backgroundImage: `url(${preview})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {image.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {image.helper}
                </Typography>
                <input
                  ref={(node) => { fileInputs.current[image.slot] = node; }}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    handleImageUpload(image.slot, file);
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                    disabled={Boolean(imageBusy)}
                    onClick={() => fileInputs.current[image.slot]?.click()}
                  >
                    Upload
                  </Button>
                  {uploaded && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ImageIcon />}
                      disabled={Boolean(imageBusy)}
                      onClick={() => handleImageClear(image.slot)}
                    >
                      Use placeholder
                    </Button>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Preview Section */}
      <Card sx={{ bgcolor: 'background.default' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Preview
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Browser Tab Title:
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="body2">
                  {formData.app_title || 'OC-LANgenthal Vol. 3 - 01.01.26 - 04.01.26'}
                </Typography>
              </Paper>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Navigation Title:
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formData.nav_title || 'OC-LANgenthal'}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SystemSettings;

